import { components } from "@certn/api/generated-schemas/client-portal";
import {
  ActionList,
  ActionListItem,
  AvatarWithName,
  Card,
  GhostButton,
  NeutralTag,
  Text,
} from "@certn/cascadia-react";
import { parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Schemas } from "../../../types";

interface AboutCardProps {
  cases:
    | readonly components["schemas"]["PeopleProfileCaseDetails"][]
    | undefined;
  viewPerson: (arg0: string) => void;
}

export function AboutCard({ cases, viewPerson }: AboutCardProps) {
  const { t } = useTranslation();
  const groups: string[] = [];
  const checks: string[] = [];
  const contacts: components["schemas"]["LinkedCase"][] = [];
  const [tags, setTags] = useState<string[]>([]);

  cases?.map((caseDetails: any) => {
    caseDetails.customer_group_name &&
      groups.push(caseDetails.customer_group_name);
    caseDetails.checks && checks.push(...caseDetails.checks);
    caseDetails.primary_case && contacts.push(caseDetails.primary_case);
    caseDetails.linked_cases && contacts.push(...caseDetails.linked_cases);
  });

  useEffect(() => {
    if (!cases) return;
    let allTags: string[] = [];
    cases.forEach((c: Schemas["PeopleProfileCaseDetails"]) => {
      allTags = allTags.concat(c.tags);
    });
    const uniqueTags = Array.from(new Set(allTags));
    setTags(uniqueTags);
  }, [cases]);

  return (
    <Card
      className={styles.cardContainer}
      type="large"
      responsive
      testID="case-history-card"
    >
      <Text style="global/heading-large" testID="about-card-title">
        {t("About")}
      </Text>
      <div className={styles.infoDetailsList}>
        <Text style="global/heading-medium" testID="about-card-groups-label">
          {t("Groups")}
        </Text>
        <Text
          style="global/body-secondary"
          color="text-weak"
          testID="about-card-groups-list"
        >
          {[...new Set(groups)].join(", ")}
        </Text>
      </div>
      <div className={styles.infoDetailsList}>
        <Text style="global/heading-medium" testID="about-card-checks-label">
          {t("Checks")}
        </Text>
        <Text
          style="global/body-secondary"
          color="text-weak"
          testID="about-card-checks-list"
        >
          {[...new Set(checks)].join(", ")}
        </Text>
      </div>
      {tags.length > 0 && (
        <div className={styles.infoDetailsList}>
          <Text style="global/heading-medium" testID="about-card-checks-label">
            {t("Tags")}
          </Text>
          <div className={styles.tagsContainer}>
            {tags.map((tag) => {
              return (
                <NeutralTag type="subtle" key={tag}>
                  {tag}
                </NeutralTag>
              );
            })}
          </div>
        </div>
      )}
      {contacts.length > 0 && (
        <div className={styles.infoDetailsList}>
          <Text
            style="global/heading-medium"
            testID="about-card-related-contacts-label"
          >
            {t("Related contacts")}
          </Text>
          <ActionList hideBorder>
            {[
              ...new Map(
                contacts?.map((contact) => [contact.short_id, contact])
              ).values(),
            ].map((contact, i) => {
              return (
                <ActionListItem
                  key={i}
                  title={
                    <AvatarWithName
                      name={
                        contact.applicant_name.given_name ||
                        contact.applicant_name.family_name
                          ? parseName(contact.applicant_name)
                          : contact.invite_email_address
                      }
                      subtitle={t("Co-applicant")}
                      textStyle="global/heading-medium"
                      testID="related-contact"
                    />
                  }
                  trailing={
                    <GhostButton
                      testID="related-contact-view-button"
                      onPress={() => viewPerson(contact.applicant_id)}
                    >
                      <Text
                        style="global/body-secondary-link"
                        color="text-weak"
                      >
                        {t("View")}
                      </Text>
                    </GhostButton>
                  }
                />
              );
            })}{" "}
          </ActionList>
        </div>
      )}
    </Card>
  );
}

const styles = {
  cardContainer: css({
    display: "flex",
    flexDirection: "column",
    padding: `${"var(--space-5)"} ${"var(--space-4)"}`,
    gap: "var(--space-4)",
  }),
  infoDetailsList: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  tagsContainer: css({
    display: "flex",
    flexDirection: "row",
    gap: "var(--space-2)",
    flexWrap: "wrap",
  }),
};
